import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-hot-toast";
import { BaseLivlyApiResponse } from "@/types/Base";
import { BASE_API_URL } from "@/utils/constants";
import type { Notification } from "@/routes/notifications";
import { Spinner } from "@/components/Spinner";

interface ResidentPostNotificationPromptProps {
  open: boolean;
  leaseId: number;
  onComplete: () => void;
}

async function enableResidentPostNotifications(
  notifications: Notification[]
): Promise<Notification[]> {
  const RESIDENT_POST_TYPE_NAME = "Community Feed Resident Post";
  const COMMUNITY_FEED_NAME = "Community Feed";

  const updated = notifications.map((notification) => {
    if (notification.title.toLowerCase().includes(COMMUNITY_FEED_NAME.toLowerCase())) {
      return {
        ...notification,
        channels: notification.channels.map((channel) => {
          if (channel.name === "Email" && channel.canToggle) {
            return { ...channel, enabled: true };
          }
          return channel;
        }),
        types: notification.types?.map((type) => {
          if (type.name !== RESIDENT_POST_TYPE_NAME) return type;
          return {
            ...type,
            channels: type.channels.map((channel) => {
              if (channel.name === "Email" && channel.canToggle) {
                return { ...channel, enabled: true };
              }
              return channel;
            }),
          };
        }),
      };
    }
    return notification;
  });

  const result = await axios.patch<BaseLivlyApiResponse<Notification[]>>(
    `${BASE_API_URL}/livly/users/me/notifications`,
    updated
  );

  return result.data.Data;
}

export default function ResidentPostNotificationPrompt({
  open,
  leaseId,
  onComplete,
}: ResidentPostNotificationPromptProps) {
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: async () => {
      const notifications = queryClient.getQueryData<Notification[]>([
        "notifications",
        leaseId,
      ]);
      if (!notifications) {
        // Fetch fresh if not cached
        const result = await axios.get<BaseLivlyApiResponse<Notification[]>>(
          `${BASE_API_URL}/livly/users/me/notifications`
        );
        return enableResidentPostNotifications(result.data.Data);
      }
      return enableResidentPostNotifications(notifications);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      toast.success("Notification preferences updated!");
      onComplete();
    },
    onError: () => {
      toast.error("There was an error updating notifications");
    },
  });

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onComplete}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all sm:p-8">
                <div className="space-y-4">
                  <Dialog.Title
                    as="h2"
                    className="text-2xl font-bold text-gray-900"
                  >
                    Stay in the loop
                  </Dialog.Title>

                  <p className="text-gray-600">
                    You&rsquo;ll always be notified when your property staff
                    posts announcements to the feed. Would you also like to be
                    notified when other residents make posts?
                  </p>

                  <p className="text-sm text-gray-400">
                    You can update this anytime in notification settings
                  </p>

                  <div className="flex flex-col gap-3 pt-2">
                    <button
                      type="button"
                      className="w-full rounded-full bg-[#F9877E] px-6 py-3 text-base font-medium text-white hover:bg-[#ff7a70] transition-colors disabled:opacity-50"
                      onClick={() => mutate()}
                      disabled={isLoading}
                      tabIndex={0}
                      aria-label="Yes, notify me of resident posts"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Spinner /> Updating...
                        </span>
                      ) : (
                        "Yes, notify me"
                      )}
                    </button>
                    <button
                      type="button"
                      className="w-full rounded-full border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={onComplete}
                      disabled={isLoading}
                      tabIndex={0}
                      aria-label="No thanks, skip resident post notifications"
                    >
                      No thanks
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
